import React, { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import useSWRMutation from 'swr/mutation';
import * as Yup from 'yup';
import { KeyedMutator } from 'swr';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { Field, Form, Formik } from 'formik';

import { useApi } from '../../../context';
import { useData } from '../../../state/selectors';
import {
  AchRelationship,
  AchRelationshipApprovalMethods,
  AchRelationshipType,
  AchBankAccountTypes,
  CreateAchRelationshipViaMicroDepositRequest,
} from '@tradingblock/types';
import { DefaultApplicationModel } from '../../../types';
import config from '../../../config';
import { ApiResponse } from '@tradingblock/api';
import { Button, FormGroup, Loading, StepperField, Text, TextboxField, useLocalize } from '@tradingblock/components';
import { FundYourAccountView, FundYourAccountViews } from '../FundYourAccount';

const StyledMicroDepositsAchRelationship = styled.div`
  button {
    width: 100%;
  }

  .loading-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

interface FormValues {
  bankAccountOwnerName: string;
  bankAccount: string;
  confirmBankAccount: string;
  bankAccountType: AchRelationshipType;
  bankName: string;
  bankRoutingNumber: string;
  confirmBankRoutingNumber: string;
  nickName: string;
}

interface MicroDepositsAchRelationshipProps extends LocalizeContextProps {
  getAchRelationshipsData: KeyedMutator<ApiResponse<AchRelationship[]> | null>;
  setCurrentFundYourAccountView: Dispatch<SetStateAction<FundYourAccountView>>;
}

const MicroDepositsAchRelationship: FunctionComponent<MicroDepositsAchRelationshipProps> = ({
  translate,
  getAchRelationshipsData,
  setCurrentFundYourAccountView,
}) => {
  const api = useApi();

  const application = useData(d => d.data.application);

  const { getTranslationId } = useLocalize();

  const { accountId } = application || DefaultApplicationModel;

  const [errorSubmittingFundingMethodRequest, setErrorSubmittingFundingMethodRequest] = useState<boolean>(false);

  const {
    trigger: triggerCreateAchRelationship,
    error: errorCreatingAchRelationship,
    isMutating: isCreatingAchRelationship,
  } = useSWRMutation(
    `${config.tradingApi}/Cashiering/${accountId}/AchRelationships`,
    (
      _url: string,
      {
        arg,
      }: {
        arg: { accountId: number; createAchRelationshipRequestPayload: CreateAchRelationshipViaMicroDepositRequest };
      }
    ) => api.cashiering.ach.create(arg.accountId, arg.createAchRelationshipRequestPayload)
  );

  const onSubmit = async (values: FormValues) => {
    if (accountId) {
      setErrorSubmittingFundingMethodRequest(false);

      const { responseCode: createAchRelationshipResponseCode } = (await triggerCreateAchRelationship({
        accountId,
        createAchRelationshipRequestPayload: {
          bankAccountOwnerNameSecret: values.bankAccountOwnerName,
          bankAccountSecret: values.bankAccount,
          bankAccountType: values.bankAccountType,
          bankName: values.bankName,
          bankRoutingNumber: values.bankRoutingNumber,
          nickName: values.nickName,
          approvalMethod: AchRelationshipApprovalMethods.MicroDeposit,
        },
      })) as ApiResponse<AchRelationship>;

      if (createAchRelationshipResponseCode === 0) {
        getAchRelationshipsData();
      }

      if (createAchRelationshipResponseCode !== 0) {
        setErrorSubmittingFundingMethodRequest(true);
      }
    }
  };

  useEffect(() => {
    if (errorCreatingAchRelationship) {
      setErrorSubmittingFundingMethodRequest(true);
    }
  }, [errorCreatingAchRelationship]);

  return (
    <StyledMicroDepositsAchRelationship>
      {isCreatingAchRelationship && (
        <div className="loading-container">
          <Loading />
        </div>
      )}

      {!isCreatingAchRelationship && (
        <>
          <p>
            <Text id="fundYourAccountComponent.verifyAccountByConfirmingMicroDeposits" />
          </p>

          <Formik
            validateOnChange
            validationSchema={Yup.object<FormValues>().shape({
              bankAccountOwnerName: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string),
              bankAccount: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string)
                .oneOf([Yup.ref('confirmBankAccount')], translate(
                  getTranslationId({ id: 'fundYourAccountComponent.accountNumbersDoNotMatch', type: 'error' })
                ) as string),
              confirmBankAccount: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string)
                .oneOf([Yup.ref('bankAccount')], translate(
                  getTranslationId({ id: 'fundYourAccountComponent.accountNumbersDoNotMatch', type: 'error' })
                ) as string),
              bankAccountType: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string),
              bankName: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string),
              bankRoutingNumber: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string)
                .oneOf([Yup.ref('confirmBankRoutingNumber')], translate(
                  getTranslationId({ id: 'fundYourAccountComponent.routingNumbersDoNotMatch', type: 'error' })
                ) as string),
              confirmBankRoutingNumber: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string)
                .oneOf([Yup.ref('bankRoutingNumber')], translate(
                  getTranslationId({ id: 'fundYourAccountComponent.routingNumbersDoNotMatch', type: 'error' })
                ) as string),
              nickName: Yup.string()
                .trim()
                .required(translate(getTranslationId({ id: 'defaults.required', type: 'error' })) as string),
            })}
            onSubmit={onSubmit}
            initialValues={
              {
                bankAccountOwnerName: '',
                bankAccount: '',
                confirmBankAccount: '',
                bankAccountType: AchBankAccountTypes.Checking,
                bankName: '',
                bankRoutingNumber: '',
                confirmBankRoutingNumber: '',
                nickName: '',
              } as FormValues
            }
          >
            <Form translate={undefined}>
              <FormGroup>
                <label htmlFor="bankAccountType">
                  <Text id="fundYourAccountComponent.accountType" />
                </label>
                <Field
                  component={StepperField}
                  id="bankAccountType"
                  options={[AchBankAccountTypes.Checking, AchBankAccountTypes.Savings].map(achRelationshipType => ({
                    value: achRelationshipType,
                    label: <Text id={`fundYourAccountComponent.${achRelationshipType}`} />,
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="bankRoutingNumber"
                  placeholder={<Text id="fundYourAccountComponent.routingNumber" />}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="confirmBankRoutingNumber"
                  placeholder={<Text id="fundYourAccountComponent.confirmRoutingNumber" />}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="bankAccount"
                  placeholder={<Text id="fundYourAccountComponent.accountNumber" />}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="confirmBankAccount"
                  placeholder={<Text id="fundYourAccountComponent.confirmAccountNumber" />}
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="bankName"
                  placeholder={<Text id="fundYourAccountComponent.bankName" />}
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="bankAccountOwnerName"
                  placeholder={<Text id="fundYourAccountComponent.bankAccountOwnerNameOrTitle" />}
                />
              </FormGroup>
              <FormGroup>
                <Field
                  component={TextboxField}
                  id="nickName"
                  placeholder={<Text id="fundYourAccountComponent.accountNickname" />}
                />
              </FormGroup>

              <p>
                <Text id="fundYourAccountComponent.newFundingMethodRequest" />
              </p>

              {errorSubmittingFundingMethodRequest && (
                <p className="error">
                  <Text id="fundYourAccountComponent.issueSubmittingFundingMethodRequest" type="error" />
                </p>
              )}

              <Button className="btn-primary" type="submit">
                <Text id="btns.submitFundingMethodRequest" />
              </Button>
            </Form>
          </Formik>

          <div className="progress-stepper-navigation">
            <Button
              className="btn-link btn-link-solo"
              onClick={() => setCurrentFundYourAccountView(FundYourAccountViews.FundingMethods)}
            >
              <Text id="btns.backToFundingMethods" />
            </Button>
          </div>
        </>
      )}
    </StyledMicroDepositsAchRelationship>
  );
};

export default withLocalize(MicroDepositsAchRelationship);
